import { useEffect, useLayoutEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { StavbaProvider } from '../../features/stavba-context/StavbaContext';
import { useAuth, type User } from '../../features/auth/AuthContext';
import { api } from '../../lib/api';
import { SupportCorner } from '../SupportCorner';
import styles from './AppShell.module.css';

/**
 * Vzhled per účet: světlý režim (`data-theme="light"`) a bílé dokumenty
 * (`data-doc-white`) na <html>. Jen uvnitř aplikace — úvodka a login zůstávají tmavé.
 */
function useAppearance() {
  const { user, updateUser } = useAuth();
  const light = user?.ui_theme === 'light';
  const docWhite = !!user?.ui_doc_white;

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (light) root.dataset.theme = 'light';
    else delete root.dataset.theme;
    if (docWhite) root.dataset.docWhite = '1';
    else delete root.dataset.docWhite;
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', light ? '#ffffff' : '#1a2230');
    return () => {
      delete root.dataset.theme;
      delete root.dataset.docWhite;
      meta?.setAttribute('content', '#1a2230');
    };
  }, [light, docWhite]);

  // Uživatel je v localStorage z přihlášení — jednou načti čerstvý stav,
  // ať se volba z jiného zařízení projeví i tady.
  useEffect(() => {
    let cancelled = false;
    api
      .get<User>('/auth/me')
      .then((u) => {
        if (!cancelled && (u.ui_theme !== user?.ui_theme || u.ui_doc_white !== user?.ui_doc_white)) updateUser(u);
      })
      .catch(() => {
        /* offline / 401 řeší api vrstva */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function AppShell() {
  useAppearance();
  return (
    <StavbaProvider>
      <div className={styles.shell}>
        <aside className={styles.sidebarSlot}>
          <Sidebar />
        </aside>
        <header className={styles.topbarSlot}>
          <TopBar />
        </header>
        <main className={styles.mainSlot}>
          <Outlet />
        </main>
        <SupportCorner context="app" />
      </div>
    </StavbaProvider>
  );
}
